import classNames from 'classnames';
import { ButtonHTMLAttributes, forwardRef, ReactNode, useMemo } from 'react';

import { ButtonAlignment, ButtonColor, ButtonSize, ButtonVariant } from '../../utils/constants';
export { ButtonAlignment, ButtonColor, ButtonSize, ButtonVariant };
interface IButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  alignment?: ButtonAlignment;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
  rounded?: boolean;
  disableText?: string;
  additionalClasses?: string;
  loading?: boolean;
  selected?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, IButtonProps>(function Button(
  {
    className,
    children,
    variant = ButtonVariant.FILLED,
    color = ButtonColor.DEFAULT,
    size = ButtonSize.MD,
    alignment = ButtonAlignment.CENTER,
    iconLeft,
    iconRight,
    fullWidth,
    rounded,
    disabled,
    loading,
    selected,
    ...rest
  },
  ref
) {
  const classes = useMemo(() => {
    const classes = [];

    if (variant === ButtonVariant.SIDEBAR) {
      classes.push('hover:bg-primary-50 px-4 py-3.5 font-medium');
      classes.push(selected ? 'text-gray-900 bg-primary-50' : 'text-gray-600 bg-white');
      return classes;
    }

    switch (color) {
      case ButtonColor.ORANGE:
        if (variant === ButtonVariant.FILLED) {
          classes.push('text-white bg-orange-700');
          classes.push('hover:bg-orange-800');
          classes.push('disabled:bg-orange-300');
        } else if (variant === ButtonVariant.OUTLINED) {
          classes.push('text-orange-600 border border-orange-600');
          classes.push('hover:text-orange-700 hover:border-orange-700');
          classes.push('disabled:text-orange-300 disabled:border-orange-300');
        } else {
          classes.push('text-orange-600');
          classes.push('disabled:text-orange-300');
        }
        break;
      case ButtonColor.BLUE:
        if (variant === ButtonVariant.FILLED) {
          classes.push('text-gray-600 bg-blue-200');
          classes.push('hover:bg-blue-300');
        } else if (variant === ButtonVariant.OUTLINED) {
          classes.push('text-blue-200 border border-blue-200');
          classes.push('hover:text-blue-300 hover:border-blue-300');
        }
        break;
      case ButtonColor.GREEN:
        if (variant === ButtonVariant.FILLED) {
          classes.push('text-gray-600 bg-green-200');
          classes.push('hover:bg-green-300');
        } else if (variant === ButtonVariant.OUTLINED) {
          classes.push('text-green-200 border border-green-200');
          classes.push('hover:text-green-300 hover:border-green-300');
        }
        break;
      case ButtonColor.WHITE:
        classes.push('text-gray-600 bg-white');
        classes.push('hover:bg-white');
        break;
      default:
        if (variant === ButtonVariant.FILLED) {
          classes.push('text-gray-600 bg-gray-100');
          classes.push('hover:bg-gray-200');
          classes.push('disabled:bg-gray-200');
        } else if (variant === ButtonVariant.OUTLINED) {
          classes.push('text-gray-500 border border-gray-500');
          classes.push('hover:text-gray-600 hover:border-gray-600');
        }
    }

    if (disabled) {
      classes.push('disabled:border-gray-200 disabled:text-gray-100');
    }

    switch (size) {
      case ButtonSize.CTA:
        classes.push('px-8 py-3');
        break;
      case ButtonSize.CARD:
        classes.push('px-4 py-4');
        break;
      case ButtonSize.BROWSE:
        classes.push('px-8 py-2');
        break;
      case ButtonSize.LARGE:
        classes.push('px-4 py-2');
        break;
      case ButtonSize.CTA_LONG:
        classes.push('px-12 py-2');
        break;
      case ButtonSize.BOX:
        classes.push('px-9 py-8');
        break;
      case ButtonSize.SMALL:
        classes.push('px-0');
        break;
      default:
        classes.push('px-3.5 py-2');
    }

    switch (variant) {
      case ButtonVariant.PRIMARY:
        classes.push('text-white bg-primary-900 rounded-lg');
        classes.push('hover:bg-primary-800');
        classes.push('focus:border-coolBlue-300 focus:bg-primary-800');
        classes.push('disabled:text-gray-100 disabled:bg-primary-300 disabled:border-primary-300');
        break;
      case ButtonVariant.SECONDARY:
        classes.push('text-white border border-coolBlue-200 bg-coolBlue-200 rounded-lg');
        classes.push('hover:bg-coolBlue-300');
        classes.push('focus:border-primary-700 focus:bg-coolBlue-300');
        classes.push('disabled:text-gray-100 disabled:bg-coolBlue-100 disabled:border-coolBlue-100');
        break;
      case ButtonVariant.TERTIARY:
        classes.push('text-gray-700 border border-gray-300 bg-transparent rounded-lg');
        classes.push('hover:text-gray-600 hover:border-gray-600');
        classes.push('focus:border-coolBlue-300');
        classes.push('disabled:text-gray-300 disabled:border-gray-300');
        break;
      case ButtonVariant.QUATERNARY:
        classes.push('text-gray-600 border-none bg-transparent rounded-lg');
        classes.push('hover:border-none');
        classes.push('disabled:text-orange-300 disabled:border-none');
        break;
    }

    return classes;
  }, [color, size, variant, disabled, selected]);

  return (
    <button
      ref={ref}
      type="button"
      className={classNames(
        'relative min-w-[5rem] inline-flex items-center disabled:cursor-not-allowed',
        alignment,
        classes,
        {
          'w-full': fullWidth,
          'rounded-full': rounded
        },
        'text-h4',
        className
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {iconLeft}
      {children}
      {iconRight}
    </button>
  );
});
